import { FC, useEffect, useMemo, useState } from 'react';
import { Grid2 as Grid } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { useAttioValuationContext } from 'dashboard409a/context/AttioValuationContext';
import { useCompanyMeasurementDateByAttioValuationId } from 'dashboard409a/hooks/useCompanyMeasurementDateByAttioValuationId';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { getValuationStatusOptions, updateValuationDealStage, updateValuationStatus } from 'dashboard409a/services/attio-common';
import { getAttioValuationDetail } from 'dashboard409a/services/attio-operation';
import { AttioValuationFormattedResponse } from 'dashboard409a/services/attio.types';
import { useCreateFulfillment, useFulfillmentList } from 'dashboard409a/services/fulfillment';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { updateGlobalState } from 'dashboard409a/states/features/global';
import { getValuationScalarOpinion } from 'dashboard409a/states/features/valuation';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import { Logger } from 'utilities';
import {
  EmptyOperationsMessage,
  ModelStatus,
  OperationWidgetDrawer,
  ValuationDealInfo,
  ValuationStatusTracker,
  ValuedEntity,
  ValuedInformation,
} from './components';
import { modelStatusMap } from './components/ModelStatus/ModelStatus.constants';
import { SpacedContainer, StyledDivRoot, StyledTypography } from './Operations.styles';

const Operations: FC = () => {
  const { valuationId, companySlug } = useParams();
  const location = useLocation();
  const { setCurrentAttioCompanyName, setCurrentAttioValuation } = useAttioValuationContext();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<AppDispatchType>();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Get company information from Redux store
  const { currentCompanyMeasurementDateId } = useSelector((state: RootStateType) => state.global);
  const companies = useSelector((state: RootStateType) => state.user.standalone_companies_permissions);

  // Reset scroll to top when navigating to this page, or scroll to hash if present
  useEffect(() => {
    // Check if there's a hash in the URL (e.g., #model-status)
    const { hash } = location;
      
    if (hash) {
      // Find the element with the matching ID
      const targetElement = document.querySelector(hash);
      if (targetElement) {
        // Scroll to the target element
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
      
    // If no hash, reset scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
    // Reset any scrollable containers
    const scrollContainers = document.querySelectorAll('[role="region"], .MuiTableContainer-root, .MuiGrid-root');
    scrollContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        const element = container;
        element.scrollTop = 0;
      }
    });
    
  }, [location, valuationId]);
  
  // Find the current company from the URL slug
  const currentCompany = useMemo(
    () => companies?.find(company => company.slug === companySlug) || null,
    [companies, companySlug]
  );

  const {
    data,
    isError,
    error,
    isLoading = false,
  } = useQuery<AttioValuationDetailResponse, Error>({
    queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
    queryFn: () => getAttioValuationDetail(valuationId as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!valuationId, // Only run query if valuationId exists
  });

  // Fetch company measurement date by attio_valuation_id if not available in global state
  const { 
    data: companyMeasurementDateByAttioId, 
    error: companyMeasurementDateError 
  } = useCompanyMeasurementDateByAttioValuationId();

  // Fetch status options from Attio API
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Transform AttioStatusOption to StatusOption
  const statusOptions = attioStatusOptions.map(option => ({
    id: option.id.option_id,
    label: option.title,
    isArchived: option.is_archived,
  }));

  // Use the App company ID for fulfillment operations (not the Attio valued entity ID)
  // The fulfillment API expects the app company ID from the standalone_companies_permissions
  const appCompanyId = currentCompany?.id?.toString();

  // Fetch fulfillment data to check if model support exists
  const {
    refetch: refetchFulfillments,
  } = useFulfillmentList(appCompanyId);

  // Mutation for creating fulfillment
  const createFulfillmentMutation = useCreateFulfillment(appCompanyId);

  // Set up Redux state for the current company and get measurement dates
  useEffect(() => {
    if (currentCompany?.id) {
      dispatch(updateGlobalState({ currentCompany }));
      dispatch(getValuationScalarOpinion(currentCompany.id));
    }
  }, [currentCompany, dispatch]);

  useEffect(() => {
    if (data){
      setCurrentAttioValuation(data as AttioValuationFormattedResponse);
      if (data?.valuedEntity.companyName) {
        setCurrentAttioCompanyName(data?.valuedEntity.companyName);
      }
    }
    
  }, [data, setCurrentAttioCompanyName, setCurrentAttioValuation]);

  if (isError) {
    Logger.error('Error fetching Attio valuations:', error);
  }

  const handleStatusChange = async (statusId: string) => {
    if (!valuationId) {
      return;
    }

    // Don't update if it's the same status
    if (data?.statusId === statusId) {
      return;
    }

    setIsUpdatingStatus(true);

    try {
      await updateValuationStatus(valuationId, statusId);

      // Invalidate queries in parallel for better performance
      const invalidationPromises = [
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ];

      await Promise.all(invalidationPromises);

      // Find the selected status label for the success message
      const selectedStatus = statusOptions.find(option => option.id === statusId);
      const statusLabel = selectedStatus?.label || 'status';

      enqueueSnackbar(`Status has been updated to ${statusLabel}`, { variant: 'success' });
    } catch (statusError) {
      Logger.error('Error updating valuation status:', statusError);
      enqueueSnackbar('Failed to update status. Please try again.', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  };  

  const handleDealStageChange = async (dealStageId: string) => {
    if (!dealStageId || !data?.deal?.dealId) {
      return;
    }
    await updateValuationDealStage(data?.deal?.dealId, dealStageId);
    await queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
      exact: false,
    });
    enqueueSnackbar('Deal stage has been updated', { variant: 'success' });
  };

  // Handle model support creation
  const handleCreateModelSupport = async () => {
    if (!appCompanyId) {
      enqueueSnackbar('Unable to create model support: Company information not available', { 
        variant: 'error' 
      });
      return;
    }

    try {
      // Use currentCompanyMeasurementDateId from global state, or fall back to the one fetched by attio_valuation_id
      const measurementDateId = currentCompanyMeasurementDateId || companyMeasurementDateByAttioId?.id;
      if (!measurementDateId) {
        if (companyMeasurementDateError) {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. The measurement date for this valuation (${valuationId}) was not found. Please check if the valuation ID is correct.`,
            { 
              variant: 'error',
              autoHideDuration: 10000,
            }
          );
        } else {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. No measurement dates exist for this company. Please create a measurement date first in the company dashboard.`,
            { 
              variant: 'warning',
              autoHideDuration: 8000,
            }
          );
        }
        return;
      }

      await createFulfillmentMutation.mutateAsync({
        company_measurement_date: measurementDateId,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT], // Start with status 0 (draft)
        supporting_due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow's date in YYYY-MM-DD format
        reviewers: [],
        preparers: [],
      });

      // Refetch fulfillment data to update the UI
      refetchFulfillments();
    } catch (fulfillmentError) {
      Logger.error('Failed to create model support:', fulfillmentError);
      // Error handling is already done in the mutation
    }
  };

  // If no valuationId is provided, show empty message
  if (!valuationId) {
    return <EmptyOperationsMessage />;
  }

  return (
    <StyledDivRoot container spacing={1}>
      <Grid size={{ md: 8, sm: 12 }}>
        <ValuedEntity valuedEntity={data?.valuedEntity} isLoading={isLoading} />
        <ValuedInformation valuedInformation={data?.valuedInformation} isLoading={isLoading} />
        <ValuationDealInfo deal={data?.deal} isLoading={isLoading} onDealStageChange={handleDealStageChange} />
      </Grid>
      <Grid size={{ md: 4, sm: 12 }}>
        <SpacedContainer>
          <StyledTypography>Status</StyledTypography>
          <SpacedContainer>
            <ValuationStatusTracker
              statusOptions={statusOptions}
              currentStatusId={data?.statusId}
              onStatusChange={handleStatusChange}
              isLoading={isLoading || isLoadingStatusOptions || isUpdatingStatus}
            />
          </SpacedContainer>
        </SpacedContainer>
      </Grid>
      <Grid size={{ md: 12, sm: 12 }}>
        <OperationWidgetDrawer
          valuationId={valuationId}
          dealId={data?.deal?.dealId}
          valuedEntityId={data?.valuedEntity?.valuedEntityId}
          primaryContactId={data?.valuedEntity?.primaryContactId}
          isLoading={isLoading}
        />
      </Grid>
    </StyledDivRoot>
  );
};

export default Operations;
